import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SocialPostMedia } from 'fym-shared/src/music24/socialPostFormat';
import { TypedMemo } from '../../../common/types';
import { platformMeta, platformSoftFill } from '../platformMeta';
import { ScheduledPostFormat, SerializedSocialChannel, SocialPlatform } from '../types';
import { composerFormatLine } from './composerFormatLine';
import {
  composerFormatCannot,
  composerFormatHints,
  composerFormatLabels,
  composerFormatRefusals,
  composerFormatUnknownForShort,
} from './composerFormatMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerFormatLinesProps {
  /** All five platforms, connected or not, in the platform order. */
  channels: SerializedSocialChannel[];
  /** The platforms the post goes to. */
  platforms: SocialPlatform[];
  /** The chosen platforms that the format cannot reach, such as a story on TikTok. */
  blockedPlatforms?: SocialPlatform[];
  /** The attached media, or null. The line updates when it changes. */
  media: SocialPostMedia | null;
  /** True while the browser measures the video: the hint "could not measure" waits. */
  isMeasuring: boolean;
  format: ScheduledPostFormat;
}

/**
 * One format line per selected channel, below the channel chips: "Goes out as a Reel", a hint
 * when a limit changed the result, or "Cannot go out" with the reason (design e1, e2).
 */
const ComposerFormatLinesComponent: React.FC<ComposerFormatLinesProps> = ({
  channels,
  platforms,
  blockedPlatforms,
  media,
  isMeasuring,
  format,
}) => {
  const intl = useIntl();

  const lines = useMemo(
    () =>
      channels
        .filter(({ platform }) => platforms.includes(platform) || blockedPlatforms?.includes(platform) === true)
        .map((channel) => {
          const meta = platformMeta[channel.platform];
          const line = composerFormatLine(channel.platform, media, format);
          // While the video loads, its measures are not known yet. That is not a failed measure.
          const hint = line.hint === 'unknownMeasures' && isMeasuring ? null : line.hint;
          const hintMessage =
            hint === 'unknownMeasures' && channel.platform === 'youtube'
              ? composerFormatUnknownForShort.hint
              : hint === null
                ? null
                : composerFormatHints[hint];
          return (
            <div key={channel.platform} className={styles.formatLine} data-testid={`format-line-${channel.platform}`}>
              <span
                className={styles.formatMark}
                style={{ background: platformSoftFill(channel.platform), color: meta.color }}
              >
                <FontAwesomeIcon icon={meta.icon} />
              </span>
              <div className={styles.formatText}>
                <b>
                  {intl.formatMessage(
                    line.format === null ? composerFormatCannot.label : composerFormatLabels[line.format]
                  )}
                </b>
                {hintMessage === null ? null : <small>{intl.formatMessage(hintMessage)}</small>}
                {line.refusal === null ? null : (
                  <small className={styles.formatRefusal}>
                    {intl.formatMessage(composerFormatRefusals[line.refusal], {
                      platform: meta.name,
                      ratio: line.ratio ?? '',
                      limit: line.limit ?? '',
                    })}
                  </small>
                )}
              </div>
              <span className={styles.formatHandle}>{channel.handle ?? ''}</span>
            </div>
          );
        }),
    [blockedPlatforms, channels, format, intl, isMeasuring, media, platforms]
  );

  if (lines.length === 0) {
    return null;
  }
  return <div className={styles.formatLines}>{lines}</div>;
};

export const ComposerFormatLines = TypedMemo(ComposerFormatLinesComponent);
